import { memo, useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Typography, makeStyles } from '@material-ui/core';
import { api_jobs_public } from 'constants/apiRoutes';
import http from '../../../utility/http';
import RichTextPreview from '../../shared/richTextPreview';
import TextBoxWithTooltip from '../../shared/textBoxWithTooltip';
import { APP_PAGES } from '../../../constants/pages';
import { useTranslations } from '../../../utility/useTranslations';
import CustomButton from '../../shared/customButton';
import { getJobItemsFirstPart, getJobItemsSecondPart } from './config';
import { KADAR_LANDING } from '../../../constants/appConfig';
import { ReactComponent as LogoIcon } from '../../../assets/icons/kadar_logo_mark_app.svg';

const CONTENT_MAX_WIDTH = 1200;

const useStyles = makeStyles(
  ({ palette: { primary }, spacing, breakpoints }) => ({
    main: {
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',
      height: '100vh',
      width: '100%',
      maxWidth: '100vw',
      '@supports (height: 100dvh)': {
        height: '100dvh',
      },
      overflow: 'hidden',
    },
    pageBody: {
      flex: '1 1 auto',
      display: 'flex',
      justifyContent: 'center',
      minWidth: 0,
      minHeight: 0,
      overflowY: 'auto',
      overflowX: 'hidden',
      padding: spacing(4, 2),
      [breakpoints.up('sm')]: {
        padding: spacing(6, 4),
      },
      [breakpoints.up('md')]: {
        padding: spacing(10, 11),
      },
    },
    wrapper: {
      width: '100%',
      maxWidth: CONTENT_MAX_WIDTH,
      gap: 32,
      display: 'grid',
      justifyContent: 'center',
      height: 'fit-content',
      boxSizing: 'border-box',
    },
    titleWrapper: {
      alignItems: 'center',
      justifyContent: 'center',
      display: 'flex',
      paddingBottom: spacing(4),
      borderBottom: `1px solid ${primary.bluish7}`,
      maxWidth: CONTENT_MAX_WIDTH,
      width: '100%',
      margin: '0 auto',
    },
    title: {
      fontSize: 28,
      width: '100%',
      maxWidth: CONTENT_MAX_WIDTH,
      textAlign: 'center',
      whiteSpace: 'normal',
      overflow: 'visible',
      textOverflow: 'unset',
      wordBreak: 'break-word',
      [breakpoints.up('sm')]: {
        fontSize: 40,
      },
      [breakpoints.up('md')]: {
        fontSize: 50,
      },
    },
    buttonWrapper: {
      justifyContent: 'center',
      marginTop: 32,
      display: 'flex',
    },
    applyButton: {
      width: 120,
      backgroundColor: primary.green5,
      borderColor: primary.green5,
      color: primary.bluish1,
      fontSize: 16,
      fontFamily: 'ProximaNova-Bold',
      '&:hover': {
        boxShadow: `0 0 0 1px ${primary.green4}`,
        backgroundColor: primary.green4,
      },
    },
    disableDescription: {
      marginTop: spacing(2),
      justifyContent: 'center',
      display: 'flex',
    },
    disableLabel: {
      color: primary.red1,
    },
    info: {
      width: '100%',
      maxWidth: CONTENT_MAX_WIDTH,
      gap: 12,
      display: 'grid',
      height: 'fit-content',
      boxSizing: 'border-box',
    },
    header: {
      display: 'grid',
      justifyContent: 'center',
      width: '100%',
    },
    infoWrapper: {
      display: 'flex',
      flexDirection: 'column',
      gap: 12,
      [breakpoints.up('sm')]: {
        flexDirection: 'row',
      },
    },
    richTextWrapper: {
      display: 'grid',
      gap: 12,
      minWidth: 0,
      width: '100%',
      overflowWrap: 'break-word',
      wordBreak: 'break-word',
    },
    subtitleWrapper: {
      width: '100%',
      [breakpoints.up('sm')]: {
        minWidth: 'fit-content',
        width: 'auto',
      },
    },
    link: {
      display: 'flex',
      alignItems: 'center',
      flexShrink: 0,
      gap: 8,
      textDecoration: 'none',
      width: '100%',
      boxSizing: 'border-box',
      backgroundColor: primary.white,
      '&:hover': {
        textDecoration: 'underline',
      },
      padding: spacing(3, 2),
      [breakpoints.down('sm')]: {
        paddingBottom: 'calc(24px + env(safe-area-inset-bottom, 0px))',
      },
      [breakpoints.up('sm')]: {
        padding: spacing(6, 4),
      },
      [breakpoints.up('md')]: {
        padding: spacing(6, 11),
      },
    },
    logoIcon: {
      width: 24,
      height: 24,
    },
    jobDetailsWrapper: {
      paddingTop: spacing(6),
      display: 'grid',
      gap: 12,
    },
  })
);

const JOB_UNAVAILABLE_PATH = '/job-unavailable';

const PublicJobPage = () => {
  const classes = useStyles();
  const translations = useTranslations(APP_PAGES.PUBLIC_JOB);
  const { token } = useParams();
  const navigate = useNavigate();

  const [job, setJob] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isJobAvailable, setIsJobAvailable] = useState(true);

  const today = new Date();
  const isArchived = job.archived_at && new Date(job.archived_at) <= today;
  const isEnded = new Date(job.date_to) <= today;
  const isDisabledApply = isArchived || isEnded;
  const hasButtonOnBottom = job.description || job.requirements || job.benefits;

  const getInitialData = useCallback(async () => {
    try {
      const { data } = await http.get(api_jobs_public(token));
      setJob(data);
    } catch (error) {
      if (error?.response?.status === 404) {
        setIsJobAvailable(false);
        navigate(JOB_UNAVAILABLE_PATH, { replace: true });
      }
    } finally {
      setIsLoading(false);
    }
  }, [navigate, token]);

  useEffect(() => {
    getInitialData();
  }, [getInitialData]);

  const renderBasic = () => {
    return Object.entries(getJobItemsFirstPart(translations, job)).map(
      ([label, value], index) => {
        return value ? (
          <div key={`job_basic_${index}`} className={classes.infoWrapper}>
            <div className={classes.subtitleWrapper}>
              <Typography variant="subtitle1">{`${label}:`}</Typography>
            </div>
            <Typography variant="body1">{value}</Typography>
          </div>
        ) : null;
      }
    );
  };

  const renderDescriptions = () => {
    return Object.entries(getJobItemsSecondPart(translations, job)).map(
      ([label, value], index) => {
        return value ? (
          <div
            key={`job_description_${index}`}
            className={classes.richTextWrapper}
          >
            <div className={classes.subtitleWrapper}>
              <Typography variant="subtitle1">{label}</Typography>
            </div>
            <RichTextPreview variant="body1" text={value} />
          </div>
        ) : null;
      }
    );
  };

  const renderApplyButton = () => {
    return isDisabledApply ? (
      <div className={classes.buttonWrapper}>
        <CustomButton
          className={classes.applyButton}
          type="withTextLightRounded"
          disabled
          onClick={() => {}}
        >
          {translations.apply}
        </CustomButton>
      </div>
    ) : (
      <div className={classes.buttonWrapper}>
        <Link to={`/job/apply/${token}`}>
          <CustomButton
            className={classes.applyButton}
            type="withTextLightRounded"
            onClick={() => {}}
          >
            {translations.apply}
          </CustomButton>
        </Link>
      </div>
    );
  };

  return (
    <div className={classes.main}>
      <div className={classes.pageBody}>
        {!isLoading && isJobAvailable && (
          <div className={classes.wrapper}>
            <div className={classes.header}>
              <div className={classes.titleWrapper}>
                <TextBoxWithTooltip
                  className={classes.title}
                  text={job?.title}
                  variant="h1"
                />
              </div>
              {renderApplyButton()}
              {isDisabledApply && (
                <div className={classes.disableDescription}>
                  <Typography className={classes.disableLabel} variant="body2">
                    {translations.disabledDescription}
                  </Typography>
                </div>
              )}
            </div>
            <div className={classes.info}>
              {renderBasic()}
              <div className={classes.jobDetailsWrapper}>
                {renderDescriptions()}
              </div>
            </div>
            {hasButtonOnBottom && renderApplyButton()}
          </div>
        )}
      </div>
      <a
        className={classes.link}
        href={KADAR_LANDING}
        target="_blank"
        rel="noopener noreferrer"
      >
        <LogoIcon className={classes.logoIcon} />
        <Typography variant="body2">{translations.poweredBy}</Typography>
      </a>
    </div>
  );
};

export default memo(PublicJobPage);
